/**
 * 「闹钟」 section: repeat weekdays, enable toggle, sound, delete confirmation
 * and the native-bridge/fallback status.
 *
 * `next_at` returned by the daemon is authoritative; the form's "预计下次"
 * line is explicitly a local preview. The Android shell's bridge is used when
 * present; otherwise the section labels the in-page reminder as unreliable.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  ALARM_SOUND_OPTIONS,
  ALARM_WEEKDAY_SHORT,
  alarmCounts,
  alarmFormValuesFromAlarm,
  alarmSoundLabel,
  emptyAlarmForm,
  formatAlarmTime,
  formatRepeatDays,
  nextAlarmAt,
  nextEnabledAlarm,
  normalizeRepeatDays,
  validateAlarmForm,
  type AlarmDraft,
  type AlarmFormErrors,
  type AlarmFormValues,
} from '../lib/alarm';
import { parseTimeInput } from '../lib/datetime';
import { formatClock, formatRelative } from '../lib/format';
import { nowSeconds } from '../lib/id';
import {
  FALLBACK_ALARM_NOTICE,
  LUMINIS_ALARM_FIRED_EVENT,
  alarmDeliveryChannel,
  fallbackNotificationPermission,
  type AlarmFiredEventDetail,
  type AlarmNotificationPermission,
} from '../lib/native-alarms';
import type { Alarm } from '../lib/types';
import { useApp } from '../state/AppState';
import { ConfirmDialog } from './ConfirmDialog';
import { Icon } from './Icon';

const INPUT_CLASS =
  'w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-slate-100 outline-none placeholder:text-slate-600 focus:border-indigo-400/50';
const LABEL_CLASS = 'text-[11px] text-slate-400';

const PERMISSION_LABELS: Record<AlarmNotificationPermission, string> = {
  granted: '通知已授权',
  default: '通知未授权',
  denied: '通知已被拒绝',
  unsupported: '当前环境不支持通知',
};

function WeekdayChips({
  days,
  onChange,
}: {
  days: readonly number[];
  onChange(days: number[]): void;
}) {
  return (
    <div className="mt-1 flex flex-wrap gap-1.5">
      <button
        type="button"
        aria-pressed={days.length === 0}
        onClick={() => onChange([])}
        className={`rounded-full border px-2.5 py-1 text-[11px] ${
          days.length === 0
            ? 'border-indigo-400/50 bg-indigo-400/15 text-indigo-100'
            : 'border-white/10 bg-white/5 text-slate-400'
        }`}
      >
        不重复
      </button>
      {ALARM_WEEKDAY_SHORT.map((weekday) => {
        const active = days.includes(weekday.day);
        return (
          <button
            key={weekday.day}
            type="button"
            aria-pressed={active}
            onClick={() => {
              const next = active
                ? days.filter((day) => day !== weekday.day)
                : [...days, weekday.day];
              onChange(normalizeRepeatDays(next));
            }}
            className={`h-8 w-8 rounded-full border text-[11px] ${
              active
                ? 'border-indigo-400/50 bg-indigo-400/15 text-indigo-100'
                : 'border-white/10 bg-white/5 text-slate-400'
            }`}
          >
            {weekday.label}
          </button>
        );
      })}
    </div>
  );
}

function AlarmForm({
  initial,
  busy,
  onSave,
  onCancel,
}: {
  initial: Alarm | null;
  busy: boolean;
  onSave(draft: AlarmDraft): Promise<boolean>;
  onCancel(): void;
}) {
  const [values, setValues] = useState<AlarmFormValues>(() =>
    initial === null ? emptyAlarmForm() : alarmFormValuesFromAlarm(initial),
  );
  const [errors, setErrors] = useState<AlarmFormErrors>({});
  const [message, setMessage] = useState<string | null>(null);

  const update = <Key extends keyof AlarmFormValues>(
    key: Key,
    value: AlarmFormValues[Key],
  ): void => {
    setValues((previous) => ({ ...previous, [key]: value }));
  };

  const soundOptions = useMemo(() => {
    const options = [...ALARM_SOUND_OPTIONS];
    if (!options.some((option) => option.value === values.sound)) {
      options.push({ value: values.sound, label: values.sound });
    }
    return options;
  }, [values.sound]);

  const preview = useMemo(() => {
    const time = parseTimeInput(values.time);
    if (!time.ok) return null;
    return nextAlarmAt(
      { hour: time.hour, minute: time.minute, repeat_days: values.repeat_days },
      nowSeconds(),
    );
  }, [values.time, values.repeat_days]);

  const submit = async (): Promise<void> => {
    const validation = validateAlarmForm(values);
    if (!validation.ok) {
      setErrors(validation.errors);
      setMessage(validation.message);
      return;
    }
    setErrors({});
    setMessage(null);
    const saved = await onSave(validation.value);
    if (!saved) setMessage('保存失败，请查看上方错误提示');
  };

  return (
    <div data-testid="alarm-form" className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
      <div className="space-y-2.5">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div>
            <label className={LABEL_CLASS} htmlFor="alarm-label">
              名称
            </label>
            <input
              id="alarm-label"
              type="text"
              value={values.label}
              maxLength={80}
              placeholder="闹钟"
              onChange={(event) => update('label', event.target.value)}
              className={`mt-1 ${INPUT_CLASS}`}
            />
            {errors.label ? <p className="mt-1 text-[10px] text-rose-300">{errors.label}</p> : null}
          </div>
          <div>
            <label className={LABEL_CLASS} htmlFor="alarm-time">
              时间
            </label>
            <input
              id="alarm-time"
              type="time"
              value={values.time}
              onChange={(event) => update('time', event.target.value)}
              className={`mt-1 ${INPUT_CLASS}`}
            />
            {errors.time ? <p className="mt-1 text-[10px] text-rose-300">{errors.time}</p> : null}
          </div>
        </div>

        <div>
          <span className={LABEL_CLASS}>重复</span>
          <WeekdayChips
            days={values.repeat_days}
            onChange={(days) => update('repeat_days', days)}
          />
        </div>

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div>
            <label className={LABEL_CLASS} htmlFor="alarm-sound">
              铃声
            </label>
            <select
              id="alarm-sound"
              value={values.sound}
              onChange={(event) => update('sound', event.target.value)}
              className={`mt-1 ${INPUT_CLASS}`}
            >
              {soundOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {errors.sound ? <p className="mt-1 text-[10px] text-rose-300">{errors.sound}</p> : null}
          </div>
          <label className="mt-5 flex items-center justify-between gap-3 rounded-xl border border-white/5 bg-white/[0.03] px-3 py-2">
            <span className="text-xs text-slate-200">启用</span>
            <input
              type="checkbox"
              className="h-5 w-5 accent-indigo-500"
              checked={values.enabled}
              onChange={(event) => update('enabled', event.target.checked)}
            />
          </label>
        </div>

        <p className="text-[10px] text-slate-500">
          预计下次：
          {preview === null ? '—' : `${formatClock(preview)}（${formatRelative(preview)}）`}
          <span className="ml-1 text-slate-600">本地预览，daemon 返回的 next_at 为准</span>
        </p>

        {message ? (
          <p className="rounded-lg border border-rose-400/25 bg-rose-400/10 px-2.5 py-1.5 text-[11px] text-rose-200">
            {message}
          </p>
        ) : null}

        <div className="flex gap-2">
          <button
            type="button"
            data-testid="alarm-save"
            disabled={busy}
            onClick={() => void submit()}
            className="flex-1 rounded-xl bg-indigo-500 py-2.5 text-sm font-medium text-white disabled:opacity-40"
          >
            {busy ? '保存中…' : initial === null ? '添加闹钟' : '保存修改'}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onCancel}
            className="rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-slate-300 disabled:opacity-40"
          >
            取消
          </button>
        </div>
      </div>
    </div>
  );
}

function AlarmRow({
  alarm,
  now,
  channel,
  busy,
  readOnly,
  onToggle,
  onEdit,
  onDelete,
}: {
  alarm: Alarm;
  now: number;
  channel: 'native' | 'pwa-fallback';
  busy: boolean;
  readOnly: boolean;
  onToggle(alarm: Alarm): void;
  onEdit(alarm: Alarm): void;
  onDelete(alarm: Alarm): void;
}) {
  const effectiveNext = alarm.enabled ? alarm.next_at : null;
  return (
    <li
      data-testid="alarm-row"
      data-id={alarm.id}
      data-enabled={alarm.enabled ? 'true' : 'false'}
      className={`rounded-xl border px-3 py-2.5 ${
        alarm.enabled ? 'border-white/10 bg-white/[0.03]' : 'border-white/5 bg-white/[0.02]'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <p
              className={`font-mono text-2xl font-semibold leading-none ${
                alarm.enabled ? 'text-slate-100' : 'text-slate-500'
              }`}
            >
              {formatAlarmTime(alarm.hour, alarm.minute)}
            </p>
            <p className="min-w-0 truncate text-sm text-slate-200">{alarm.label}</p>
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <span className="rounded-full border border-white/10 bg-white/5 px-1.5 py-0.5 text-[9px] text-slate-400">
              {formatRepeatDays(alarm.repeat_days)}
            </span>
            <span className="rounded-full border border-white/10 bg-white/5 px-1.5 py-0.5 text-[9px] text-slate-400">
              {alarmSoundLabel(alarm.sound)}
            </span>
            <span
              className={`rounded-full border px-1.5 py-0.5 text-[9px] ${
                channel === 'native'
                  ? 'border-emerald-400/30 bg-emerald-400/10 text-emerald-200'
                  : 'border-amber-400/30 bg-amber-400/10 text-amber-200'
              }`}
            >
              {channel === 'native' ? '原生闹钟' : '页面提醒（不可靠）'}
            </span>
            {alarm.enabled ? (
              <span className="text-[9px] text-slate-500">
                {effectiveNext === null
                  ? '等待 daemon 计算下次时间'
                  : `下次 ${formatClock(effectiveNext)} · ${formatRelative(effectiveNext, now)}`}
              </span>
            ) : (
              <span className="text-[9px] text-slate-500">已停用</span>
            )}
          </div>
        </div>

        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <button
            type="button"
            data-testid="alarm-toggle"
            role="switch"
            aria-checked={alarm.enabled}
            aria-label={alarm.enabled ? '停用闹钟' : '启用闹钟'}
            disabled={busy || readOnly}
            onClick={() => onToggle(alarm)}
            className={`relative h-6 w-11 rounded-full border transition disabled:opacity-40 ${
              alarm.enabled
                ? 'border-emerald-400/40 bg-emerald-400/25'
                : 'border-white/15 bg-white/10'
            }`}
          >
            <span
              className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${
                alarm.enabled ? 'left-[22px]' : 'left-1'
              }`}
              style={{ height: 18, width: 18 }}
            />
          </button>
          <div className="flex items-center gap-1">
            <button
              type="button"
              data-testid="alarm-edit"
              aria-label="编辑闹钟"
              disabled={busy || readOnly}
              onClick={() => onEdit(alarm)}
              className="rounded-lg border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] text-slate-300 disabled:opacity-40"
            >
              编辑
            </button>
            <button
              type="button"
              data-testid="alarm-delete"
              aria-label="删除闹钟"
              disabled={busy || readOnly}
              onClick={() => onDelete(alarm)}
              className="rounded-lg border border-rose-400/30 bg-rose-400/10 px-2 py-0.5 text-[10px] text-rose-100 disabled:opacity-40"
            >
              删除
            </button>
          </div>
        </div>
      </div>
    </li>
  );
}

export function AlarmSection() {
  const {
    todoAlarmAvailable,
    todoAlarmUnavailableReason,
    alarms,
    alarmSyncedAt,
    alarmLoading,
    alarmError,
    alarmDeliveryError,
    refreshAlarms,
    createAlarm,
    updateAlarm,
    deleteAlarm,
    network,
  } = useApp();
  const [formMode, setFormMode] = useState<'closed' | 'create' | 'edit'>('closed');
  const [editing, setEditing] = useState<Alarm | null>(null);
  const [deleting, setDeleting] = useState<Alarm | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [fired, setFired] = useState<AlarmFiredEventDetail | null>(null);
  const [permission, setPermission] = useState<AlarmNotificationPermission>(() =>
    fallbackNotificationPermission(),
  );
  const [channel] = useState(() => alarmDeliveryChannel());
  const now = nowSeconds();
  const counts = alarmCounts(alarms);
  const enabled = useMemo(() => alarms.filter((alarm) => alarm.enabled), [alarms]);
  const disabled = useMemo(() => alarms.filter((alarm) => !alarm.enabled), [alarms]);
  const next = useMemo(() => nextEnabledAlarm(alarms, now), [alarms, now]);
  const readOnly = !todoAlarmAvailable;

  // In-page fallback fire -> transient banner. Native fires are acked by
  // AppState; the same event also arrives there through the bridge callback.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onFired = (event: Event): void => {
      const detail = (event as CustomEvent<AlarmFiredEventDetail>).detail;
      if (detail === undefined || typeof detail.id !== 'string') return;
      setFired({
        id: detail.id,
        label: typeof detail.label === 'string' && detail.label.length > 0 ? detail.label : '闹钟',
        at: typeof detail.at === 'number' && Number.isFinite(detail.at) ? detail.at : nowSeconds(),
      });
    };
    window.addEventListener(LUMINIS_ALARM_FIRED_EVENT, onFired);
    return () => window.removeEventListener(LUMINIS_ALARM_FIRED_EVENT, onFired);
  }, []);

  useEffect(() => {
    if (fired === null) return undefined;
    const timer = setTimeout(() => setFired(null), 12_000);
    return () => clearTimeout(timer);
  }, [fired]);

  const requestPermission = async (): Promise<void> => {
    if (typeof Notification === 'undefined') return;
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
    } catch {
      setPermission(fallbackNotificationPermission());
    }
  };

  const save = async (draft: AlarmDraft): Promise<boolean> => {
    const result =
      formMode === 'edit' && editing !== null
        ? await updateAlarm(editing.id, draft)
        : await createAlarm(draft);
    if (!result.ok) return false;
    setFormMode('closed');
    setEditing(null);
    return true;
  };

  const toggleEnabled = async (alarm: Alarm): Promise<void> => {
    if (readOnly) return;
    setBusyId(alarm.id);
    await updateAlarm(alarm.id, { enabled: !alarm.enabled });
    setBusyId(null);
  };

  const confirmDelete = async (): Promise<void> => {
    if (deleting === null) return;
    setBusyId(deleting.id);
    const result = await deleteAlarm(deleting.id);
    setBusyId(null);
    if (result.ok) setDeleting(null);
  };

  const cacheLabel =
    alarmSyncedAt === null
      ? null
      : `上次同步 ${formatClock(alarmSyncedAt)}${
          !network.online || alarmError !== null ? ' · 离线缓存，可能不是最新' : ''
        }`;

  return (
    <div className="space-y-3">
      <section className="rounded-2xl border border-white/10 bg-[#0f1629] p-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h2 className="text-sm font-semibold text-slate-100">闹钟</h2>
            <p className="mt-0.5 text-[11px] text-slate-500">
              启用 {counts.enabled} · 共 {counts.total}
              {next !== null
                ? ` · 最近 ${formatAlarmTime(next.hour, next.minute)} ${next.label}`
                : ''}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <button
              type="button"
              data-testid="alarm-refresh"
              disabled={!todoAlarmAvailable || alarmLoading}
              onClick={() => void refreshAlarms()}
              className="flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] text-slate-300 disabled:opacity-40"
            >
              <Icon name="refresh" className={`h-3 w-3 ${alarmLoading ? 'animate-spin' : ''}`} />
              刷新
            </button>
            <button
              type="button"
              data-testid="alarm-new"
              disabled={!todoAlarmAvailable || formMode !== 'closed'}
              onClick={() => {
                setEditing(null);
                setFormMode('create');
              }}
              className="flex items-center gap-1 rounded-full border border-indigo-400/40 bg-indigo-400/15 px-2.5 py-1 text-[11px] text-indigo-100 disabled:opacity-40"
            >
              <Icon name="plus" className="h-3 w-3" />
              新建
            </button>
          </div>
        </div>

        <div
          data-testid="alarm-channel"
          className={`mt-3 rounded-xl border px-3 py-2 text-[11px] leading-relaxed ${
            channel === 'native'
              ? 'border-emerald-400/25 bg-emerald-400/10 text-emerald-200'
              : 'border-amber-400/25 bg-amber-400/10 text-amber-200'
          }`}
        >
          {channel === 'native' ? (
            <p>
              已连接 Android
              原生闹钟桥（`window.LuminisNativeAlarms`）：由系统调度，锁屏/后台也能触发。
            </p>
          ) : (
            <>
              <p>{FALLBACK_ALARM_NOTICE}</p>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px] text-amber-200/80">
                <span>{PERMISSION_LABELS[permission]}</span>
                {permission === 'default' || permission === 'denied' ? (
                  <button
                    type="button"
                    onClick={() => void requestPermission()}
                    className="rounded-lg border border-amber-400/40 bg-amber-400/10 px-2 py-0.5 text-[10px] text-amber-100"
                  >
                    开启通知
                  </button>
                ) : null}
              </div>
            </>
          )}
        </div>

        {!todoAlarmAvailable ? (
          <p
            data-testid="alarm-unavailable"
            className="mt-3 rounded-xl border border-amber-400/25 bg-amber-400/10 px-3 py-2 text-[11px] leading-relaxed text-amber-200"
          >
            {todoAlarmUnavailableReason ?? '闹钟当前不可用'}
            {alarms.length > 0 ? ' 下方为只读缓存。' : ''}
          </p>
        ) : null}

        {alarmError ? (
          <div
            data-testid="alarm-error"
            className="mt-3 rounded-xl border border-rose-400/25 bg-rose-400/10 px-3 py-2 text-[11px] leading-relaxed text-rose-200"
          >
            <p className="break-words">{alarmError}</p>
            {todoAlarmAvailable ? (
              <button
                type="button"
                onClick={() => void refreshAlarms()}
                disabled={alarmLoading}
                className="mt-2 rounded-lg border border-rose-400/40 bg-rose-400/10 px-2.5 py-1 text-[11px] text-rose-100 disabled:opacity-40"
              >
                重试
              </button>
            ) : null}
          </div>
        ) : null}

        {alarmDeliveryError ? (
          <p
            data-testid="alarm-delivery-error"
            className="mt-3 rounded-xl border border-rose-400/25 bg-rose-400/10 px-3 py-2 text-[11px] leading-relaxed text-rose-200"
          >
            {alarmDeliveryError}
          </p>
        ) : null}

        {fired ? (
          <section
            data-testid="alarm-fired"
            className="mt-3 flex items-start justify-between gap-2 rounded-xl border border-indigo-400/30 bg-indigo-400/10 px-3 py-2 text-[11px] text-indigo-100"
          >
            <p>
              闹钟已触发：{fired.label} · {formatClock(fired.at)}
              {channel === 'native' ? '（原生桥）' : '（页面提醒）'}
            </p>
            <button
              type="button"
              aria-label="关闭闹钟提示"
              onClick={() => setFired(null)}
              className="shrink-0 rounded-lg border border-white/10 bg-white/5 px-1.5 py-0.5 text-[10px]"
            >
              关闭
            </button>
          </section>
        ) : null}

        {formMode !== 'closed' ? (
          <div className="mt-3">
            <AlarmForm
              key={formMode === 'edit' ? `edit-${editing?.id ?? ''}` : 'create'}
              initial={formMode === 'edit' ? editing : null}
              busy={alarmLoading}
              onSave={save}
              onCancel={() => {
                if (alarmLoading) return;
                setFormMode('closed');
                setEditing(null);
              }}
            />
          </div>
        ) : null}

        <div className="mt-3 space-y-3">
          {alarmLoading && alarms.length === 0 ? (
            <p className="rounded-xl border border-white/5 bg-white/[0.02] px-3 py-4 text-center text-[11px] text-slate-500">
              正在读取闹钟…
            </p>
          ) : null}

          {!alarmLoading && alarms.length === 0 && todoAlarmAvailable ? (
            <section
              data-testid="alarm-empty"
              className="rounded-xl border border-white/5 bg-white/[0.02] px-3 py-6 text-center"
            >
              <Icon name="alarm" className="mx-auto h-7 w-7 text-slate-600" />
              <p className="mt-2 text-sm text-slate-300">还没有闹钟</p>
              <p className="mt-1 text-[11px] text-slate-500">
                新建闹钟，选择重复星期与铃声；Android 壳会接管系统级提醒。
              </p>
            </section>
          ) : null}

          <AlarmList
            title="已启用"
            alarms={enabled}
            now={now}
            channel={channel}
            busyId={busyId}
            readOnly={readOnly}
            onToggle={(alarm) => void toggleEnabled(alarm)}
            onEdit={(alarm) => {
              if (readOnly) return;
              setEditing(alarm);
              setFormMode('edit');
            }}
            onDelete={(alarm) => {
              if (readOnly) return;
              setDeleting(alarm);
            }}
          />
          <AlarmList
            title="已停用"
            alarms={disabled}
            now={now}
            channel={channel}
            busyId={busyId}
            readOnly={readOnly}
            onToggle={(alarm) => void toggleEnabled(alarm)}
            onEdit={(alarm) => {
              if (readOnly) return;
              setEditing(alarm);
              setFormMode('edit');
            }}
            onDelete={(alarm) => {
              if (readOnly) return;
              setDeleting(alarm);
            }}
          />
        </div>

        {cacheLabel ? <p className="mt-3 text-[10px] text-slate-500">{cacheLabel}</p> : null}
      </section>

      <ConfirmDialog
        open={deleting !== null}
        title="删除这个闹钟？"
        danger
        busy={alarmLoading}
        confirmLabel="删除"
        onCancel={() => {
          if (alarmLoading) return;
          setDeleting(null);
        }}
        onConfirm={() => void confirmDelete()}
      >
        <p className="text-[11px] leading-relaxed text-slate-400">
          「{deleting?.label ?? ''}」
          {deleting ? `（${formatAlarmTime(deleting.hour, deleting.minute)}）` : ''}
          将从 daemon 中删除，无法恢复。
        </p>
      </ConfirmDialog>
    </div>
  );
}

function AlarmList({
  title,
  alarms,
  now,
  channel,
  busyId,
  readOnly,
  onToggle,
  onEdit,
  onDelete,
}: {
  title: string;
  alarms: readonly Alarm[];
  now: number;
  channel: 'native' | 'pwa-fallback';
  busyId: string | null;
  readOnly: boolean;
  onToggle(alarm: Alarm): void;
  onEdit(alarm: Alarm): void;
  onDelete(alarm: Alarm): void;
}) {
  if (alarms.length === 0) return null;
  return (
    <div>
      <div className="flex items-center gap-2 pb-1">
        <h3 className="text-[11px] font-medium text-slate-300">{title}</h3>
        <span className="rounded-full border border-white/10 bg-white/5 px-1.5 py-0.5 text-[10px] text-slate-400">
          {alarms.length}
        </span>
        <span className="h-px flex-1 bg-white/5" />
      </div>
      <ul className="space-y-2">
        {alarms.map((alarm) => (
          <AlarmRow
            key={alarm.id}
            alarm={alarm}
            now={now}
            channel={channel}
            busy={busyId === alarm.id}
            readOnly={readOnly}
            onToggle={onToggle}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))}
      </ul>
    </div>
  );
}
